import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { TicketCheckInPanel } from '@/components/tickets/TicketCheckInPanel';
import { useReissueTicketQr } from '@/data/ticketAdmission';

// Admin ticket-admission desk: scan/enter a QR to admit a guest (shared check-in
// panel, also used by faculty), plus reissue a lost ticket's QR. Gated by the
// `admin` route shell; reissue is admin/service only.
export const Route = createFileRoute('/admin/admission')({
  component: AdminAdmission,
});

function AdminAdmission() {
  return (
    <div className="space-y-10">
      <header>
        <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          Productions
        </p>
        <h1 className="mt-2">Ticket admission</h1>
        <p className="mt-1 text-[var(--color-ink-muted)]">
          Scan or enter a ticket QR code to admit a guest, or reissue a lost ticket&rsquo;s code.
        </p>
      </header>
      <TicketCheckInPanel />
      <ReissuePanel />
    </div>
  );
}

function ReissuePanel() {
  const [ticketId, setTicketId] = useState('');
  const [reason, setReason] = useState('');
  const reissue = useReissueTicketQr();

  return (
    <section className="surface-elevated space-y-4 p-5">
      <h2 className="font-[var(--font-display)] text-[var(--text-xl)]">Reissue a QR code</h2>
      <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
        Rotates the ticket&rsquo;s QR code and invalidates the old one. Use when a guest lost their
        ticket or a code may have been shared.
      </p>
      <form
        className="grid max-w-xl gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!ticketId.trim() || reissue.isPending) return;
          reissue.mutate({ ticketId: ticketId.trim(), reason });
        }}
      >
        <div className="space-y-1">
          <Label htmlFor="reissue-ticket">Ticket ID</Label>
          <Input
            id="reissue-ticket"
            value={ticketId}
            onChange={(e) => setTicketId(e.target.value)}
            placeholder="Ticket UUID"
            autoComplete="off"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="reissue-reason">Reason (optional)</Label>
          <Input
            id="reissue-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. guest lost their phone"
            autoComplete="off"
          />
        </div>
        <div>
          <Button
            type="submit"
            variant="secondary"
            disabled={reissue.isPending || !ticketId.trim()}
          >
            {reissue.isPending ? 'Reissuing…' : 'Reissue QR code'}
          </Button>
        </div>
      </form>

      <div aria-live="polite">
        {reissue.isError && (
          <p role="alert" className="text-[var(--color-danger)] text-[var(--text-sm)]">
            {reissue.error instanceof Error ? reissue.error.message : 'Reissue failed.'}
          </p>
        )}
        {reissue.data?.ok && (
          <p
            data-testid="reissue-result"
            className="text-[var(--color-success)] text-[var(--text-sm)]"
          >
            New QR code issued. The previous code no longer works.
          </p>
        )}
        {reissue.data && !reissue.data.ok && (
          <p role="alert" className="text-[var(--color-danger)] text-[var(--text-sm)]">
            {reissue.data.error === 'not_found'
              ? 'Ticket not found.'
              : 'Could not reissue this ticket.'}
          </p>
        )}
      </div>
    </section>
  );
}
